import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

/** The signed-in identity shared by frontend services and pages. */
export interface TenantIdentity {
  // Company IDs are int64 in the backend; keep their exact text in JavaScript.
  readonly companyId: string | null;
  readonly userId: number;
  readonly isPlatformStaff: boolean;
}

/** Untrusted identity fields supplied by the authentication boundary. */
export interface TenantIdentityInput {
  readonly companyId?: unknown;
  readonly userId: unknown;
  readonly isPlatformStaff?: unknown;
}

/** Auth owns updates. This client context does not grant server permissions. */
@Injectable({ providedIn: 'root' })
export class TenantContextService {
  private identity: TenantIdentity | null = null;
  private version = 0;
  private readonly changes = new Subject<void>();

  /** Future session changes only; reading the current identity does not emit. */
  readonly sessionChanged$ = this.changes.asObservable();

  get current(): TenantIdentity | null { return this.identity; }
  get currentCompanyId(): string | null { return this.identity?.companyId ?? null; }
  get currentUserId(): number | null { return this.identity?.userId ?? null; }
  get isPlatformStaff(): boolean { return this.identity?.isPlatformStaff ?? false; }
  get sessionVersion(): number { return this.version; }

  /** Every authenticated visit starts fresh, even for the same identity. */
  startSession(input: TenantIdentityInput): boolean {
    const identity = this.normalize(input);
    if (!identity) {
      this.endSession();
      return false;
    }
    this.identity = Object.freeze(identity);
    ++this.version;
    this.changes.next();
    return true;
  }

  private normalize(input: TenantIdentityInput): TenantIdentity | null {
    if (!input || typeof input !== 'object') return null;
    const userId = normalizeId(input.userId, 2147483647n);
    const companyId = normalizeId(input.companyId, 9223372036854775807n);
    const isPlatformStaff = input.isPlatformStaff === true || input.isPlatformStaff === 'true';
    if (userId === null) return null;
    // Only platform identities may omit a company; malformed IDs never become null tenants.
    if (companyId === null && !(input.companyId == null && isPlatformStaff)) return null;
    return { companyId, userId: Number(userId), isPlatformStaff };
  }

  endSession(): void {
    this.identity = null;
    ++this.version;
    this.changes.next();
  }
}

/** Reject lossy numbers and non-decimal input, then canonicalize leading zeroes. */
function normalizeId(value: unknown, maximum: bigint): string | null {
  if (typeof value === 'number' && !Number.isSafeInteger(value)) return null;
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const text = String(value).trim();
  if (!/^\d+$/.test(text)) return null;
  const id = BigInt(text);
  return id > 0n && id <= maximum ? id.toString() : null;
}
